// marks.tsx — Habits mark set, lifted VERBATIM from the Claude Design delivery
// (project 3ed5f0ef, identity "The Punch Ledger").
//
// 🔴 THE THREE DAY-STATES ARE THE MODEL. done / missed / SKIPPED — a deliberate rest day is
// not a failure, and the marks must be told apart WITHOUT colour (the board is read at a
// glance and eight habit colours sit side by side). Each is a distinct SHAPE, not a tint.
//
// Rendered as INLINE <svg>, never <img src={...}>: currentColor inside an <img> document
// resolves to black and vanishes on the dark ground (it shipped Study broken 2026-08-06).
import React from "react";

export type DayState = "done" | "missed" | "skipped";
export type CadenceId = "daily" | "weekly" | "adhoc";

type Geo = { vb: string; el: React.ReactNode };
const STATE_ART: Record<string, Geo> = {
  "done": { vb: "0 0 24 24", el: (<><circle cx="12" cy="12" r="9" fill="currentColor"></circle>
  <circle cx="12" cy="12" r="9" fill="none" stroke="rgba(0,0,0,0.24)" strokeWidth="1"></circle>
  <path d="M7.2 9.3a6.3 6.3 0 0 1 6.2-5.1" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="1.3" strokeLinecap="round"></path></>) },
  "missed": { vb: "0 0 24 24", el: (<><circle cx="12" cy="12" r="8.2" fill="none" stroke="currentColor" strokeWidth="1.6"></circle></>) },
  "skipped": { vb: "0 0 24 24", el: (<><circle cx="12" cy="12" r="8.2" fill="none" stroke="currentColor" strokeWidth="1.6"></circle>
  <line x1="8" y1="12" x2="16" y2="12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"></line></>) },
};
const CADENCE_ART: Record<string, Geo> = {
  "daily": { vb: "0 0 16 16", el: (<><rect x="3" y="3" width="10" height="10" rx="2" fill="currentColor"></rect></>) },
  "weekly": { vb: "0 0 16 16", el: (<><rect x="3" y="3" width="10" height="10" rx="2" fill="none" stroke="currentColor" strokeWidth="1.4"></rect>
  <circle cx="8" cy="8" r="1.6" fill="currentColor"></circle></>) },
  "adhoc": { vb: "0 0 16 16", el: (<><rect x="3" y="3" width="10" height="10" rx="2" fill="none" stroke="currentColor" strokeWidth="1.4" strokeDasharray="2.2 2.2"></rect></>) },
};
const MOMENT_ART: Record<string, Geo> = {
  "empty-state": { vb: "0 0 160 120", el: (<><rect x="18" y="14" width="124" height="92" rx="14" fill="none" stroke="#9B9280" strokeWidth="2"></rect>
  <circle cx="46" cy="60" r="7" fill="none" stroke="#C9BF9F" strokeWidth="1.6" strokeDasharray="2 3"></circle>
  <circle cx="70" cy="60" r="7" fill="none" stroke="#C9BF9F" strokeWidth="1.6" strokeDasharray="2 3"></circle>
  <circle cx="94" cy="60" r="7" fill="none" stroke="#C9BF9F" strokeWidth="1.6" strokeDasharray="2 3"></circle>
  <circle cx="118" cy="60" r="7" fill="none" stroke="#C9BF9F" strokeWidth="1.6" strokeDasharray="2 3"></circle></>) },
  "first-week": { vb: "0 0 220 120", el: (<><rect x="18" y="14" width="184" height="92" rx="14" fill="none" stroke="#9B9280" strokeWidth="2"></rect>
  <circle cx="46" cy="60" r="9" fill="#C1560B"></circle>
  <circle cx="72" cy="60" r="9" fill="#C1560B"></circle>
  <circle cx="98" cy="60" r="9" fill="#C1560B"></circle>
  <circle cx="124" cy="60" r="9" fill="#C1560B"></circle>
  <circle cx="150" cy="60" r="9" fill="#C1560B"></circle>
  <circle cx="176" cy="60" r="9" fill="none" stroke="#C9BF9F" strokeWidth="1.6" strokeDasharray="2 3"></circle></>) },
};

function Svg({ geo, className, title, size }: { geo: Geo; className?: string; title?: string; size?: number }) {
  return (
    <svg viewBox={geo.vb} width={size} height={size} className={className}
         role={title ? "img" : "presentation"} aria-label={title}
         aria-hidden={title ? undefined : true} focusable="false">
      {title ? <title>{title}</title> : null}{geo.el}
    </svg>
  );
}

export function DayMark({ state, className = "day-mark", size, title }:
  { state: DayState; className?: string; size?: number; title?: string }) {
  return <Svg geo={STATE_ART[state]} className={className} size={size} title={title ?? state} />;
}
export function CadenceMark({ cadence, className = "cad-mark", size, title }:
  { cadence: CadenceId; className?: string; size?: number; title?: string }) {
  return <Svg geo={CADENCE_ART[cadence] ?? CADENCE_ART.daily} className={className} size={size} title={title} />;
}
export function MomentArt({ id, className, size }:
  { id: "empty-state" | "first-week"; className?: string; size?: number }) {
  return <Svg geo={MOMENT_ART[id]} className={className} size={size} />;
}

/** For the P7 coverage assertion. */
export const STATE_KEYS = Object.keys(STATE_ART);
export const CADENCE_KEYS = Object.keys(CADENCE_ART);
export const MOMENT_KEYS = Object.keys(MOMENT_ART);
